import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '@/store/authStore';
import { subscriptionService, SubscriptionStatus } from '@/services/subscriptionService';

// Derive initial status from auth store user data to avoid flash of wrong state
function getInitialStatus(user: any): SubscriptionStatus | null {
  const sub = user?.subscription;
  if (!sub) return null;
  if (sub.plan === 'pioneer' || sub.isPioneer) {
    return {
      tier: 'pioneer',
      status: 'active',
      hasActiveSubscription: true,
      isPioneer: true,
      pioneerNumber: sub.pioneerNumber
    };
  }
  return {
    tier: (sub.plan as SubscriptionStatus['tier']) || 'free',
    status: (sub.status as SubscriptionStatus['status']) || 'inactive',
    hasActiveSubscription: sub.status === 'active' || sub.status === 'trialing'
  };
}

export function useSubscription() {
  const { token, isAuthenticated, user } = useAuthStore();
  const [status, setStatus] = useState<SubscriptionStatus | null>(() => getInitialStatus(user));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);
  const userRef = useRef(user);
  userRef.current = user;

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setStatus(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const subscriptionStatus = await subscriptionService.getStatus(token);
      setStatus(subscriptionStatus);
    } catch (err) {
      console.error('Failed to fetch subscription status:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch subscription');
      // Fall back to auth store user data if API fails
      const userSub = (userRef.current as any)?.subscription;
      if (userSub?.plan === 'pioneer' || userSub?.isPioneer) {
        setStatus({
          tier: 'pioneer',
          status: 'active',
          hasActiveSubscription: true,
          isPioneer: true,
          pioneerNumber: userSub?.pioneerNumber
        });
        setError(null); // Clear error since we have valid fallback data
      } else {
        setStatus({
          tier: 'free',
          status: 'inactive',
          hasActiveSubscription: false
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchStatus();
  }, [fetchStatus]);

  const startCheckout = async (billingCycle: 'monthly' | 'yearly') => {
    // Check authentication state, not just token presence
    if (!isAuthenticated) {
      setError('Please sign in to subscribe');
      return;
    }

    // If authenticated but no token, this is an auth state issue
    if (!token) {
      console.error('Authenticated but token missing - possible auth state issue');
      setError('Session expired. Please refresh and try again.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { url } = await subscriptionService.createCheckoutSession(token, billingCycle);
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err) {
      console.error('Failed to start checkout:', err);
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setIsLoading(false);
    }
  };

  const manageSubscription = async () => {
    // Check authentication state, not just token presence
    if (!isAuthenticated) {
      setError('Please sign in to manage subscription');
      return;
    }

    // If authenticated but no token, this is an auth state issue
    if (!token) {
      console.error('Authenticated but token missing - possible auth state issue');
      setError('Session expired. Please refresh and try again.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { url } = await subscriptionService.createPortalSession(token);
      // Redirect to Stripe Customer Portal
      window.location.href = url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
      setIsLoading(false);
    }
  };

  const hasAccess = status ? subscriptionService.hasAccess(status) : false;
  const trialDaysRemaining = status ? subscriptionService.getTrialDaysRemaining(status) : null;
  const daysUntilRenewal = status ? subscriptionService.getDaysUntilRenewal(status) : null;

  return {
    status,
    isLoading,
    error,
    hasAccess,
    trialDaysRemaining,
    daysUntilRenewal,
    isTrialing: status?.status === 'trialing',
    isPastDue: status?.status === 'past_due',
    isCancelled: status?.status === 'cancelled' || !!status?.cancelledAt,
    isActive: status?.status === 'active',
    tier: status?.tier || 'free',
    startCheckout,
    manageSubscription,
    refresh: fetchStatus,
    clearError: () => setError(null)
  };
}
